import ShopifyCartSync from '../helpers/shopifyCartSync';
import { syncAjaxToken, getCartInfo } from '../api/cart';
import { ON_CONVERSATION_CREATED } from '../constants/widgetBusEvents';

/**
 * Cart State Tracking Mixin
 * 
 * Tracks Shopify cart state and forwards cart operations from backend to theme widget.
 * Cart modifications are handled by the theme widget via Ajax API.
 * 
 * Debug Logging:
 * To enable debug logs, set window.CART_SYNC_DEBUG = true in the browser console.
 */
export default {
  data() {
    return {
      ajaxCartToken: null,
      storefrontCartId: null,
      isCartSyncing: false,
      syncDebounceTimer: null,
      syncedConversations: new Set(), // Track which conversations have been synced
      currentConversationId: null, // Track current conversation to detect changes
      debugLogging: window.CART_SYNC_DEBUG || false,
      maxTrackedConversations: 50
    };
  },
  
  computed: {
    conversationId() {
      return this.$store.getters['conversationAttributes/getConversationParams']?.id;
    }
  },
  
  watch: {
    // Watch for conversation changes
    conversationId(newId, oldId) {
      if (newId && newId !== oldId) {
        this.debugLog('🔄 Conversation changed from', oldId, 'to', newId);
        this.currentConversationId = newId;
        
        // Check if we need to sync the cart token for this new conversation
        if (this.ajaxCartToken) {
          const syncKey = `${newId}-${this.ajaxCartToken}`;
          if (!this.syncedConversations.has(syncKey)) {
            this.debugLog('📦 New conversation detected, syncing cart token');
            // Force sync bypasses the duplicate check
            this.syncAjaxTokenWithBackend(this.ajaxCartToken, true);
          } else {
            this.debugLog('✅ Cart already synced for this conversation');
          }
        }
      }
    }
  },
  
  mounted() {
    // Initialize cart sync when component mounts
    ShopifyCartSync.init();
    
    // Listen for cart updates from parent shop
    window.addEventListener('shopifyCartUpdated', this.handleShopifyCartUpdate);
    
    // Listen for conversation creation to sync stored token
    window.bus.$on('ON_CONVERSATION_CREATED', this.syncStoredTokenAfterConversationCreated);
    
    // Listen for cart updates from backend (via ActionCable)
    window.bus.$on('cart:updated', this.handleBackendCartUpdate);
    
    // Set initial conversation ID
    this.currentConversationId = this.conversationId;
    
    // Check for stored ajax token from previous session
    const storedToken = localStorage.getItem('shopify_ajax_cart_token');
    if (storedToken) {
      this.ajaxCartToken = storedToken;
      this.debugLog('📦 Found stored Ajax cart token:', storedToken);
      
      // If we have a conversation already, sync the token immediately
      if (this.conversationId) {
        this.syncAjaxTokenWithBackend(storedToken);
      }
    }
    
    // Check for existing Ajax cart token from backend
    this.checkExistingCart();
    
    // Request cart state from parent shop
    ShopifyCartSync.requestCartState();
    
    // Also request again after a short delay in case parent wasn't ready
    setTimeout(() => {
      if (!this.ajaxCartToken) {
        this.debugLog('🔄 No cart token yet, requesting again...');
        ShopifyCartSync.requestCartState();
      }
    }, 500);
  },
  
  beforeDestroy() {
    window.bus.$off('ON_CONVERSATION_CREATED', this.syncStoredTokenAfterConversationCreated);
    window.bus.$off('cart:updated', this.handleBackendCartUpdate);
    window.removeEventListener('shopifyCartUpdated', this.handleShopifyCartUpdate);
    
    // Clear any pending sync timer
    if (this.syncDebounceTimer) {
      clearTimeout(this.syncDebounceTimer);
    }
    
    // Clear Set to free memory
    this.syncedConversations.clear();
  },
  
  methods: {
    // Debug logging helper - optimized to skip args processing when disabled
    debugLog(...args) {
      // Early return to avoid any processing when debugging is off
      if (!this.debugLogging && !window.CART_SYNC_DEBUG) return;
      console.log('[CartSync]', ...args);
    },
    
    // Debug warning helper
    debugWarn(...args) {
      if (!this.debugLogging && !window.CART_SYNC_DEBUG) return;
      console.warn('[CartSync]', ...args);
    },
    
    // Debug error helper - always show errors in development
    debugError(...args) {
      if (!this.debugLogging && !window.CART_SYNC_DEBUG && process.env.NODE_ENV !== 'development') return;
      console.error('[CartSync]', ...args);
    },
    
    // Check for existing Ajax cart from shop
    async checkExistingCart() {
      const conversationId = this.conversationId; // Use computed property (cached)
      if (!conversationId) return;
      
      try {
        // Check if we have an Ajax token stored
        const { data } = await getCartInfo(conversationId);
        if (data.ajax_token) {
          this.ajaxCartToken = data.ajax_token;
          this.storefrontCartId = `gid://shopify/Cart/${data.ajax_token}`;
          this.debugLog('📦 Using existing Ajax cart token:', this.ajaxCartToken);
        } else {
          this.debugLog('📦 No Ajax cart found, waiting for customer to visit shop');
        }
      } catch (error) {
        this.debugError('Failed to get cart info:', error);
      }
    },
    
    // Handle cart updates from parent shop
    handleShopifyCartUpdate(event) {
      const { cart, ajax_token } = event.detail;
      this.debugLog('🛒 Received cart update from shop:', cart);
      this.debugLog('🔑 Ajax cart token:', ajax_token);
      
      // Only process if token is new or changed
      if (ajax_token && ajax_token !== this.ajaxCartToken) {
        const previousToken = this.ajaxCartToken;
        this.ajaxCartToken = ajax_token;
        
        // Clear previous sync tracking when token changes (new cart)
        // Only iterate if we have items to clear
        if (previousToken && this.syncedConversations.size > 0) {
          this.debugLog('🔄 Token changed, clearing previous sync tracking');
          // More efficient: create new Set with filtered items
          const newSet = new Set();
          for (const key of this.syncedConversations) {
            if (!key.endsWith(`-${previousToken}`)) {
              newSet.add(key);
            }
          }
          this.syncedConversations = newSet;
        }
        
        // Store in localStorage so it persists even before conversation exists
        localStorage.setItem('shopify_ajax_cart_token', ajax_token);
        this.debugLog('💾 Stored NEW Ajax cart token locally (was:', previousToken, ')');
        
        // Only sync with backend if conversation exists
        const conversationId = this.conversationId; // Use computed property (cached)
        if (conversationId) {
          this.debugLog('📦 Syncing NEW Ajax cart token with backend');
          this.syncAjaxTokenWithBackend(ajax_token);
        } else {
          this.debugLog('⏳ Conversation not created yet, will sync token when conversation starts');
        }
      } else if (ajax_token === this.ajaxCartToken) {
        this.debugLog('✅ Token unchanged, skipping sync');
      }
      
      // Cart state is available here if needed for display purposes
      // this.currentShopifyCart = cart;
    },
    
    // Sync Ajax cart token with backend (debounced)
    async syncAjaxTokenWithBackend(ajaxToken, forceSync = false) {
      const conversationId = this.conversationId; // Use computed property (cached)
      if (!conversationId || !ajaxToken) return;
      
      // Check if this conversation has already been synced with this token
      const syncKey = `${conversationId}-${ajaxToken}`;
      if (!forceSync && this.syncedConversations.has(syncKey)) {
        this.debugLog('✅ Token already synced for conversation:', conversationId);
        return;
      }
      
      // Prevent duplicate syncs while one is in progress
      if (this.isCartSyncing) {
        this.debugLog('⏳ Sync already in progress, skipping');
        return;
      }
      
      // Clear any pending sync timer
      if (this.syncDebounceTimer) {
        clearTimeout(this.syncDebounceTimer);
      }
      
      // Debounce the sync to prevent rapid-fire calls
      this.syncDebounceTimer = setTimeout(async () => {
        this.isCartSyncing = true;
        try {
          const { data } = await syncAjaxToken(conversationId, ajaxToken);
          this.debugLog('✅ Ajax token synced with backend for conversation:', conversationId, data);
          
          // Update storefront cart ID if provided
          if (data.storefront_cart_id) {
            this.storefrontCartId = data.storefront_cart_id;
          }
          
          // Mark this conversation-token combination as synced only if successful
          // Check for success flag or presence of storefront_cart_id as indicator of success
          if (data.success !== false && (data.success || data.storefront_cart_id)) {
            // Prevent unbounded growth of the Set
            if (this.syncedConversations.size >= this.maxTrackedConversations) {
              // Remove oldest entries (first half of the set)
              const entries = Array.from(this.syncedConversations);
              this.syncedConversations = new Set(entries.slice(Math.floor(entries.length / 2)));
            }
            this.syncedConversations.add(syncKey);
            this.debugLog('📝 Marked conversation', conversationId, 'as synced with token:', ajaxToken.substring(0, 8) + '...');
          } else {
            this.debugWarn('⚠️ Sync completed but not marked as successful:', data);
          }
        } catch (error) {
          this.debugError('Failed to sync Ajax token for conversation:', conversationId, error);
          // Don't mark as synced if there was an error
        } finally {
          this.isCartSyncing = false;
        }
      }, 300); // Wait 300ms to batch rapid updates
    },
    
    // Sync stored token after conversation is created
    async syncStoredTokenAfterConversationCreated() {
      const storedToken = localStorage.getItem('shopify_ajax_cart_token');
      if (storedToken) {
        this.debugLog('🚀 Conversation created, syncing stored Ajax cart token:', storedToken);
        // Wait a moment for conversation to be fully initialized
        setTimeout(async () => {
          const conversationId = this.conversationId; // Use computed property (cached)
          if (conversationId) {
            await this.syncAjaxTokenWithBackend(storedToken);
          }
        }, 500);
      }
    },
    
    handleBackendCartUpdate(data) {
      this.debugLog('🚀 Received cart update from backend:', data);
      
      const cartData = data;
      
      if (cartData && cartData.type === 'cart_sync') {
        this.debugLog('📤 Forwarding cart sync to parent shop:', cartData);
        ShopifyCartSync.forwardCartSyncToParent(cartData);
      }
    },
    
    shouldAutoOpenCart() {
      return false;
    }
  }
};